import { createContext, Dispatch, FC, useMemo, ReactNode, SetStateAction, useContext, useState } from 'react';

import { ApplicantRole, IncomeType, Product, QuickApplicant, QuickCreateBlancoApplication, QuickCreateMortgageApplication, MaritalStatus, Loan, Currency } from 'src/api/zrm';
import { useSettingsContext } from 'src/contexts/SettingsContext';
import BlancoNorwayForm from 'src/types/BlancoNorway/form/BlancoNorwayForm';
import BlancoSwedenForm from 'src/types/BlancoSweden/form/BlancoSwedenForm';
import MortgageNorwayForm from 'src/types/MortgageNorway/form/MortgageNorwayForm';
import calculateLengthOfStay from 'src/utils/calc/calculateLengthOfStay';
import { isNorway, isSweden } from 'src/utils/country/isCountry';
import isEmploymentNameRequired from 'src/utils/employmentType/isEmployerNameRequired';
import camelCaseToText from 'src/utils/format/camelCaseToText';
import mapLoans from 'src/utils/loans/mapLoans';
import { isMortgage, isBlanco } from 'src/utils/product/isProduct';

interface SharedDataContextProps {
  formData: any;
  setFormData: Dispatch<SetStateAction<any>>;
  preparedQuickApp: any;
}

interface SharedDataContextProviderProps {
  children: ReactNode;
}

const SharedDataContext = createContext<SharedDataContextProps>({
  formData: null,
  setFormData: () => { },
  preparedQuickApp: null,
});

/**
 * One place to store data that needs to be shared across multiple components.
 * Ex. We need form data to be accesed via form stepper, in order to skip steps.
 */
const SharedDataContextProvider: FC<SharedDataContextProviderProps> = (props) => {
  const { children } = props;
  const { country, formType, product } = useSettingsContext();
  const [formData, setFormData] = useState<any>(null);

  const isCountryNorway = isNorway();
  const isCountrySweden = isSweden();
  const isProductBlanco = isBlanco();
  const isProductMortgage = isMortgage();

  const hasEmployer = useMemo(() => {
    if (isProductMortgage) {
      return false;
    } else {
      return isEmploymentNameRequired(formData?.income?.employmentType);
    }
  }, [country, product, formData?.income?.employmentType]);


  const prepareQuickApplication = (inputData: any) => {

    let loans: Loan[] = [];
    let employmentStartDate: string = null;
    let employmentEndDate: string = null;

    if (inputData.income.employmentStartDate?.year) { employmentStartDate = new Date(Date.UTC(inputData.income.employmentStartDate.year, inputData.income.employmentStartDate.month, 1)).toISOString(); }

    if (inputData.income.employmentEndDate?.year) { employmentEndDate = new Date(Date.UTC(inputData.income.employmentEndDate.year, inputData.income.employmentEndDate.month, 1)).toISOString(); }


    let incomeAmount, yearlyIncomeAmount;

    if (isCountrySweden) {
      const data = inputData as BlancoSwedenForm;

      incomeAmount = data.income.monthlyIncome;
    } else {
      const data = inputData as BlancoNorwayForm;

      yearlyIncomeAmount = data.income.yearlyIncome;
    }


    const applicant: QuickApplicant = {
      role: ApplicantRole.MAIN_APPLICANT,
      pni: inputData.pni.pni,
      email: inputData.contact.email,
      phone_number: inputData.contact.phone,
      currently_living_in_cost: inputData.housing.livingCost,
      employer: {
        name: inputData.income.employerName || '',
        type: inputData.income.employmentType,
        start_date: employmentStartDate,
        end_date: employmentEndDate,
      },
      incomes: [
        {
          amount: !isCountryNorway ? incomeAmount : undefined,
          yearly_amount: isCountryNorway ? yearlyIncomeAmount : undefined,
          name: hasEmployer ? inputData?.income?.employerName : `${camelCaseToText(inputData?.income?.employmentType)} Income`,
          type: IncomeType.Employment,
          currency: isCountrySweden ? Currency.SEK : Currency.NOK,
        },
      ],
      housing_type: inputData.housing.livingSituation,
      marital_status: inputData.family.maritalStatus,
    };

    if (isCountryNorway) {

      const data = isProductMortgage ? inputData as MortgageNorwayForm : inputData as BlancoNorwayForm;

      applicant.citizenship = data.pni.isNorwegianCitizen;

      if (applicant.citizenship === false && data.pni.lengthOfStay) {
        applicant.immigrated_at = calculateLengthOfStay(data.pni.lengthOfStay);
      }

      loans = mapLoans(Object.values(data.loans));

      if ([MaritalStatus.Married, MaritalStatus.Partner].includes(data.family.maritalStatus)) {
        applicant.incomes.push({
          yearly_amount: data.income.spouseYearlyIncome,
          name: 'Partner Income',
          type: IncomeType.Partner,
          currency: Currency.NOK,
        });
      }

      if (data.income.hasYearlyRentIncome) {
        applicant.incomes.push({
          name: 'Rent Income',
          type: IncomeType.Rent,
          yearly_amount: data.income.yearlyRentIncome,
          currency: Currency.NOK,
        });
      }
    }

    const applicants = [applicant];

    if (inputData.coApplicant.hasCoApplicant) {
      // Employment start and end date - Co Applicant

      let coEmploymentStartDate: string = null;
      let coEmploymentEndDate: string = null;

      if (inputData.coApplicant.income.employmentStartDate?.year) { coEmploymentStartDate = new Date(Date.UTC(inputData.coApplicant.income.employmentStartDate.year, inputData.coApplicant.income.employmentStartDate.month, 1)).toISOString(); }
      if (inputData.coApplicant.income.employmentEndDate?.year) { coEmploymentEndDate = new Date(Date.UTC(inputData.coApplicant.income.employmentEndDate.year, inputData.coApplicant.income.employmentEndDate.month, 1)).toISOString(); }

      const coApplicantHasEmployer = isEmploymentNameRequired(inputData.coApplicant.income.employmentType);

      /**
       * For Sweden we take `living cost, housing type & marital status` from co-applicant data, for Norway it's same as in main applicant
       */

      const coApplicant = {
        role: ApplicantRole.CO_APPLICANT,
        pni: inputData.coApplicant.pni.pni,
        email: inputData.coApplicant.contact.email,
        phone_number: inputData.coApplicant.contact.phone,
        currently_living_in_cost: isCountrySweden || isProductMortgage ? inputData.coApplicant.housing.livingCost : inputData.housing.livingCost,
        employer: {
          name: inputData.coApplicant.income.employerName || '',
          type: inputData.coApplicant.income.employmentType,
          start_date: coEmploymentStartDate,
          end_date: coEmploymentEndDate,
        },
        incomes: [],
        housing_type: isCountrySweden || isProductMortgage ? inputData.coApplicant.housing.livingSituation : inputData.housing.livingSituation,
        marital_status: isCountrySweden || isProductMortgage ? inputData.coApplicant.family.maritalStatus : inputData.family.maritalStatus,
      } as QuickApplicant;

      if (isCountrySweden) {
        const data = inputData as BlancoSwedenForm;

        coApplicant.incomes.push(
          {
            amount: data.coApplicant.income.monthlyIncome,
            name: coApplicantHasEmployer ? inputData.coApplicant.income.employerName : `${camelCaseToText(data.coApplicant.income.employmentType)} Income`,
            type: IncomeType.Employment,
            currency: Currency.SEK,
          }
        );
      } else {
        const data = isProductMortgage ? inputData as MortgageNorwayForm : inputData as BlancoNorwayForm;

        coApplicant.citizenship = data.coApplicant.pni.isNorwegianCitizen;

        if (coApplicant.citizenship === false && data.coApplicant.pni.lengthOfStay) {
          coApplicant.immigrated_at = calculateLengthOfStay(data.coApplicant.pni.lengthOfStay);
        }

        coApplicant.incomes.push(
          {
            yearly_amount: isProductMortgage ? data.coApplicant.income.yearlyIncome : data.income.spouseYearlyIncome,
            name: coApplicantHasEmployer ? inputData.coApplicant.income.employerName : `${camelCaseToText(data.coApplicant.income.employmentType)} Income`,
            type: IncomeType.Employment,
            currency: Currency.NOK,
          }
        );
      }

      applicants.push(coApplicant);
    }

    // TODO: send ZID here
    const newLoanAmount = inputData.startPage.loanAmount - +inputData.startPage.refinanceAmount;
    const mappedData: QuickCreateMortgageApplication | QuickCreateBlancoApplication = {
      country: country,
      product: isProductBlanco ? Product.Blanco : Product.Mortgage,
      applicants,
      children: {
        childrenBelow18: +inputData.family.children,
      },
      loans,
      new_app_checklist: [],
      new_app_options: [],
      source: null,
      consents: inputData.startPage.consent ? {
        email_marketing: true,
        sms_marketing: true,
        customer_club_marketing: true,
      } : undefined,
      // if refinance is false or loanAmount is higher than refinanceAmount there is "space" for a new loan in the calculation
      new_loan: (!inputData.startPage.refinance || Math.floor(newLoanAmount / 10_000) > 0) ? {
        desired_amount: !inputData.startPage.refinance ? inputData.startPage.loanAmount : newLoanAmount,
        loan_purpose: inputData.startPage.newLoanPurpose,
      } : undefined,
      desired_amount: +inputData.startPage.loanAmount,
      desired_payback_time: inputData.startPage.repaymentTime * 12,
      form_type: formType,
    };

    if (isCountryNorway) {
      if (isProductBlanco) {
        const data = inputData as BlancoNorwayForm;

        if (data?.extraData?.isGrFirst) {
          mappedData.form_mode = 'gr_first';

          // In GR First flow there is dedicated section for new loan
          if (data?.extraData?.newLoan?.newLoan) {
            mappedData.new_loan = {
              desired_amount: +data.extraData.newLoan.newLoanAmount,
              loan_purpose: data.extraData.newLoan.newLoanPurpose,
            };
            mappedData.desired_amount = +(data.startPage.loanAmount + data.extraData.newLoan.newLoanAmount);
          } else {
            // Required to clear this field because of complicated logic in normal flow
            mappedData.new_loan = undefined;
          }
        }
      }
      if (isProductMortgage) {
        const data = inputData as MortgageNorwayForm;

        if (inputData.coApplicant.hasCoApplicant) {
          (mappedData as QuickCreateMortgageApplication).co_relationship_to_main_applicant = (inputData as MortgageNorwayForm).coApplicant.coApplicantRelationship;
        }

        if (data?.extraData?.isEvFirst) {
          mappedData.form_mode = 'ev_first';
        }
      }
    }

    return mappedData;
  };

  const preparedQuickApp = useMemo(() => {
    // TODO: add validation sche
    if (formData) {
      return prepareQuickApplication(formData);
    } else {
      return null;
    }
  }, [product, formData]);

  const value = {
    formData,
    setFormData,
    preparedQuickApp,
  };

  return (
    <SharedDataContext.Provider
      value={value}
    >
      {children}
    </SharedDataContext.Provider>
  );
};

const useSharedDataContext = () => useContext(SharedDataContext);

export { SharedDataContextProvider, useSharedDataContext };
